import type { AiManagedGroupEligibleCampaign } from "@adpilot/shared";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { DataTableFooter } from "@/components/patterns/DataTable";
import { DataTablePagination } from "@/components/patterns/DataTablePagination";
import { ErrorState } from "@/components/shared/error-state";
import { Checkbox } from "@/components/ui/checkbox";
import { Spinner } from "@/components/ui/spinner";

interface CampaignTransferPaneProps {
  kind: "available" | "selected";
  title: string;
  countLabel: string;
  actions?: ReactNode;
  rows: AiManagedGroupEligibleCampaign[];
  checkedIds: Set<string>;
  allRowsChecked: boolean;
  someRowsChecked: boolean;
  isError: boolean;
  errorMessage?: string;
  isLoading: boolean;
  emptyText: string;
  page: number;
  pageCount: number;
  pageSize: number;
  total: number;
  selectionDisabled?: boolean;
  checkboxLabel: (campaign: AiManagedGroupEligibleCampaign) => string;
  selectAllLabel: string;
  onPageChange: (page: number) => void;
  onRetry?: () => void;
  onToggleAll: (checked: boolean) => void;
  onToggle: (campaign: AiManagedGroupEligibleCampaign) => void;
}

export function CampaignTransferPane({
  kind,
  title,
  countLabel,
  actions,
  rows,
  checkedIds,
  allRowsChecked,
  someRowsChecked,
  isError,
  errorMessage,
  isLoading,
  emptyText,
  page,
  pageCount,
  pageSize,
  total,
  selectionDisabled,
  checkboxLabel,
  selectAllLabel,
  onPageChange,
  onRetry,
  onToggleAll,
  onToggle,
}: CampaignTransferPaneProps): React.ReactElement {
  const { t } = useTranslation("managedGroups");
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);
  const selectable = rows.filter((row) => kind === "selected" || row.status !== "archived");
  return (
    <div
      data-slot="campaign-transfer-pane"
      data-pane={kind}
      className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden"
    >
      <div className="flex h-9 shrink-0 items-center justify-between gap-2 border-b border-line px-3">
        <p className="text-xs font-semibold">{title}</p>
        <span className="text-xs tabular-nums text-muted-foreground">{countLabel}</span>
      </div>
      <div
        className="flex h-8 shrink-0 items-center gap-3 border-b border-line px-3 text-xs"
        aria-live="polite"
      >
        {actions}
      </div>
      <div className="flex h-7 shrink-0 items-center gap-2.5 border-b border-line bg-subtle px-3 text-xs text-muted-foreground">
        <Checkbox
          className="size-4"
          aria-label={selectAllLabel}
          checked={allRowsChecked}
          indeterminate={!allRowsChecked && someRowsChecked}
          disabled={selectionDisabled || selectable.length === 0 || isLoading || isError}
          onCheckedChange={(checked) => onToggleAll(checked === true)}
        />
        <span className="min-w-0 flex-1">{t("create.columns.campaign")}</span>
        <span className="w-16 shrink-0 text-right">{t("create.columns.targeting")}</span>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto paper-scrollbar" aria-busy={isLoading}>
        {isLoading ? (
          <div className="flex h-full items-center justify-center">
            <Spinner />
          </div>
        ) : isError ? (
          <ErrorState message={errorMessage ?? t("loadFailed")} onRetry={onRetry} />
        ) : rows.length ? (
          rows.map((campaign) => (
            <label
              key={campaign.id}
              htmlFor={`${kind}-${campaign.id}`}
              className="flex h-9 items-center gap-2.5 border-b border-line px-3 text-xs hover:bg-subtle/60"
            >
              <Checkbox
                className="size-4"
                id={`${kind}-${campaign.id}`}
                aria-label={checkboxLabel(campaign)}
                checked={checkedIds.has(campaign.id)}
                disabled={
                  selectionDisabled || (kind === "available" && campaign.status === "archived")
                }
                onCheckedChange={() => onToggle(campaign)}
              />
              <span className="min-w-0 flex-1 truncate" title={campaign.name}>
                {campaign.name}
              </span>
              <span className="w-16 shrink-0 text-right text-muted-foreground">
                {campaign.targetingType
                  ? t(`create.targeting.${campaign.targetingType}`)
                  : t("candidateFilters.unknownTargeting")}
              </span>
            </label>
          ))
        ) : (
          <div className="flex h-full items-center justify-center px-5 text-center text-xs text-muted-foreground">
            {emptyText}
          </div>
        )}
      </div>
      <DataTableFooter className="min-h-[38px] shrink-0 px-3">
        <DataTablePagination
          density="compact"
          page={page}
          pageCount={pageCount}
          summary={t("create.pagination", { from, to, total })}
          onPageChange={onPageChange}
        />
      </DataTableFooter>
    </div>
  );
}
